<template>
	<div class="w-full">
		<div
			class="relative flex h-[390px] flex-col items-center overflow-clip bg-[#FCE3EE] md:h-[180px] md:flex-row lg:flex-row xl:h-[200px]"
			:class="isPartner ? whitelabel.styles.bg.borderRadius : 'rounded'"
		>
			<!-- Right-side warm wash -->
			<div
				class="pointer-events-none absolute right-0 top-0 h-full w-full bg-[radial-gradient(ellipse_at_top_right,_rgba(255,0,34,0.35)_0%,_transparent_30%)]"
				aria-hidden="true"
			/>

			<button
				class="absolute left-0 top-0 z-20 h-full w-full"
				@click="handleBannerClick"
			/>

			<div
				class="pointer-events-none relative z-30 flex h-full w-full flex-col items-center justify-center gap-1 px-4 py-4 md:flex-row lg:gap-4"
			>
				<!-- Car + floral key visual -->
				<div
					class="relative z-10 order-2 h-full w-full md:order-1 md:w-fit lg:w-[300px]"
				>
					<div
						class="absolute h-full w-full sm:left-[180px] md:left-0"
					>
						<!-- Yellow flower -->
						<div
							class="flower-enter delay-5 absolute -left-[20px] -top-[40px] md:-top-[30px] lg:-left-[50px] lg:-top-[30px]"
						>
							<div class="flower-spin h-full w-full">
								<CarbarImage
									image="img/campaigns/spring-sale-2026/flower-yellow.webp"
									alt-text="flower yellow"
									image-width-class="w-auto"
									image-height-class="h-[180px] lg:h-[200px]"
									class="object-contain"
									:preload="true"
								/>
							</div>
						</div>

						<!-- Purple flower -->
						<div
							class="flower-enter delay-4 absolute -top-[20px] left-[100px] sm:left-[90px] md:-top-[30px] lg:-top-[10px] lg:left-[120px]"
						>
							<div
								class="flower-spin flower-spin-reverse relative"
							>
								<CarbarImage
									image="img/campaigns/spring-sale-2026/flower-purple.webp"
									alt-text="flower purple"
									image-width-class="w-auto"
									image-height-class="h-[150px]"
									class="relative object-contain"
									:preload="true"
								/>
							</div>
						</div>

						<!-- Green flower -->
						<div
							class="flower-enter delay-3 absolute -bottom-[40px] left-[180px] sm:-bottom-[50px] sm:left-[100px] lg:-bottom-[50px] lg:left-[180px]"
						>
							<div class="flower-spin relative">
								<CarbarImage
									image="img/campaigns/spring-sale-2026/flower-green.webp"
									alt-text="flower green"
									image-width-class="w-auto"
									image-height-class="h-[140px] lg:h-[180px]"
									class="relative object-contain"
									:preload="true"
								/>
							</div>
						</div>
					</div>

					<!-- Car -->
					<div
						class="car-enter-left relative z-10 flex h-full w-full items-center justify-center"
					>
						<CarbarImage
							image="img/campaigns/spring-sale-2026/img-car.webp"
							alt-text="MG3"
							image-width-class="w-auto"
							image-height-class="h-[120px] sm:h-[140px] md:h-[110px] lg:h-[130px] xl:h-[150px]"
							class="relative object-contain"
							:preload="true"
						/>
					</div>
				</div>

				<!-- Titles + offer -->
				<div
					class="relative z-10 order-1 flex w-full flex-col items-center md:order-2 md:w-auto md:flex-row md:items-center md:gap-4 xl:gap-6"
				>
					<div
						class="relative flex flex-col items-center gap-1 md:items-start"
					>
						<div
							class="slide-up delay-2 order-last text-center font-heading text-[1.8rem] font-extrabold uppercase leading-none text-[#4A1832] md:order-first md:text-left md:text-[2rem] lg:text-[2.4rem]"
						>
							Spring
							<br class="hidden md:block" />
							Sale
						</div>
						<div
							class="slide-up delay-1 text-center text-lg font-medium uppercase leading-none text-[#4A1832] md:text-left xl:text-xl"
						>
							19 Oct – 15 Nov
						</div>
					</div>

					<div
						class="slide-up delay-2 hidden h-[100px] w-[2px] shrink-0 bg-[#F95843] md:block xl:h-[120px]"
					/>

					<div
						class="slide-up delay-3 flex flex-col items-center text-left md:items-start md:gap-0.5 md:text-center"
					>
						<div
							class="font-heading text-[3rem] font-extrabold uppercase leading-none tracking-tight text-[#F95843] md:text-[2.5rem] lg:text-[3.5rem]"
						>
							50%
						</div>
						<div
							class="text-lg leading-none text-[#F95843] md:text-xl"
						>
							off upfront fee
						</div>
						<div
							class="mt-1 text-lg font-semibold uppercase leading-none text-[#F95843] md:text-left xl:text-xl"
						>
							+ Bonus 10,000KM
						</div>
					</div>
				</div>

				<!-- Cta & Promocode -->
				<div
					class="slide-in-right delay-3 pointer-events-auto relative z-30 order-last flex w-fit min-w-[150px] md:flex-col"
				>
					<div
						class="flex flex-col rounded-l-lg bg-[#F95843] px-4 py-2 md:w-full md:rounded-b-none md:rounded-t-lg lg:px-4 lg:py-3"
					>
						<template v-if="!isListingOnly">
							<span
								class="font-body text-xsm font-medium uppercase text-white"
							>
								PROMO CODE:
							</span>
							<div class="place-items-star grid">
								<Transition name="fade" mode="out-in">
									<span
										v-if="promocode"
										key="text"
										class="col-start-1 row-start-1 font-heading text-xl font-semibold leading-none tracking-wide text-white lg:text-xl"
									>
										{{ promocode }}
									</span>

									<div
										v-else
										key="loader"
										class="col-start-1 row-start-1 flex items-center"
									>
										<ContentLoader
											class="h-[18px] w-20 sm:w-[68px]"
										/>
									</div>
								</Transition>
							</div>
						</template>
						<template v-else>
							<div
								class="flex w-[120px] flex-1 items-center justify-center text-base font-medium leading-none text-white lg:w-[170px]"
							>
								Eligible vehicles only, confirmed after order
							</div>
						</template>
					</div>
					<div
						class="group flex cursor-pointer items-center justify-between gap-2 rounded-r-lg bg-[#4A1832] px-4 py-2 text-lg font-medium text-white transition-colors duration-300 hover:bg-[#1f3628] hover:text-white md:rounded-b-lg md:rounded-t-none"
						@click="handleBannerClick"
					>
						Find a car
						<IconArrowRight
							class="h-5 w-5 text-[#FCE3EE] group-hover:text-white"
						/>
					</div>
				</div>
			</div>

			<TermsAndConditions
				:is-full-width="false"
				custom-class="pb-4 md:pb-0 md:absolute md:right-4 md:bottom-2 lg:bottom-3 z-50"
				font-color="text-[#F95843]"
			>
				<CarbarLink
					text="Terms & conditions"
					text-color="text-[#F95843]"
					underline-color="decoration-[#F95843]"
					hover-color="hover:decoration-[#4A1832] hover:text-[#4A1832]"
					font-size="text-xsm"
					class="cursor-pointer"
					@click="
						goToPageAndScroll(
							'/terms-and-conditions',
							'#spring-sale-2026-promo',
							true
						)
					"
				/>
				apply.
			</TermsAndConditions>
		</div>
	</div>
</template>

<script setup lang="ts">
import IconArrowRight from '@/assets/svg/common/ico-arrow-right-menu-small.svg'
import TermsAndConditions from '@/components/fineprint/TermsAndConditions.vue'
import CarbarLink from '@/components/links/CarbarLink.vue'

const isListingOnly = useIsListingOnly()
const promocode = getPrimaryCampaignPromo()

const { partnerName } = useGetPartnerName()
const { whitelabel } = useWhitelabelsStyles()

const isPartner = !!partnerName.value

const handleBannerClick = async () => {
	const partnerUrl = whitelabel.value?.links?.partnerUrl

	if (isPartner && partnerUrl) {
		await navigateTo(`${partnerUrl}/browse`, { external: true })
	} else {
		await navigateTo('/cars/subscribe', { external: true })
	}
}
</script>

<style lang="scss" scoped>
.flower-enter {
	opacity: 0;
	transform: scale(0.4);
	animation: flower-fade-scale-in 0.45s ease-out forwards;
}

@keyframes flower-fade-scale-in {
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.flower-spin {
	width: 100%;
	height: 100%;
	animation: flower-rotate 14s linear infinite;
	animation-delay: 0.45s;
}

.flower-spin-reverse {
	animation-direction: reverse;
	animation-duration: 18s;
}

@keyframes flower-rotate {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

.car-enter-left {
	opacity: 0;
	transform: translateX(-40%);
	animation: car-fade-from-left 0.55s ease-out forwards;
}

@keyframes car-fade-from-left {
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

.slide-in-right {
	transform: translateX(30%);
	opacity: 0;
	animation: slide-in 0.5s ease forwards;
}

@keyframes slide-in {
	to {
		transform: translateX(0);
		opacity: 1;
	}
}

.slide-up {
	display: inline-block;
	transform: translateY(30px);
	margin-right: 0.35rem;
	opacity: 0;
	animation: slide-up 0.4s ease forwards;
}

@keyframes slide-up {
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

.delay-0 {
	animation-delay: 0s;
}
.delay-1 {
	animation-delay: 0.12s;
}
.delay-2 {
	animation-delay: 0.24s;
}
.delay-3 {
	animation-delay: 0.6s;
}
.delay-4 {
	animation-delay: 0.8s;
}
.delay-5 {
	animation-delay: 1s;
}
</style>
